import React from "react";
import axios from "axios";
import validator from 'validator';
import { notifications } from '@mantine/notifications';
import { Text, Box, Drawer, Button, TextInput, Divider, Textarea, Group, ActionIcon } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";
import { BE_API_THREE, utils } from "../../utils/globalVariables";
import { regexNumberOnly } from "../../utils/globalFunctions";
import StockItemData from "./StockItemData";
import ModalResponse from "../../components/general/ModalResponse";

const StockItemMoveStokRusak = ({
    stateShow,
    fnShow,
    stateData,
    fnSuccess
}) => {

    const mountedRef = React.useRef(true);
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorSubmitData: 'StockItemMoveStokRusak-1',
        successSubmitData: 'StockItemMoveStokRusak-2',
    })

    const [data, setData] = React.useState({
        submitLoading: false,

        jml: '',
        jmlError: '',

        keterangan: '',
        keteranganError: '',

        modalResponse: {
            show: false,
            type: utils.modalResponse.warning,
            hideOnClickOutSide: false,
            content: ''
        },
    });

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const submitData = () => {
        setData(d => ({ ...d, jmlError: '', keteranganError: '' }));

        if (validator.isEmpty(data.jml, { ignore_whitespace: true })) {
            setData(d => ({ ...d, jmlError: 'Jumlah wajib diisi!' }));
        } else if (validator.isEmpty(data.keterangan, { ignore_whitespace: true })) {
            setData(d => ({ ...d, keteranganError: 'Keterangan wajib diisi!' }));
        } else {

            setData(d => ({ ...d, submitLoading: true }));

            const formDT = new FormData();
            const formDTHeader = { 'Content-Type': 'application/json' }

            formDT.append('json_token', loginContext.syswebapp);
            formDT.append('stock_id', stateData.stock_id);
            formDT.append('jml', data.jml);
            formDT.append('keterangan', data.keterangan);

            axios({
                method: 'post',
                url: BE_API_THREE + '/masukkan_item_ke_stok_rusak',
                data: formDT,
                headers: formDTHeader,
            }).then((response) => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false }));

                if (response.data.flag_status === 'isset') {

                    fnSuccess();

                    notifications.show({
                        id: notificationId.current.successSubmitData,
                        title: utils.notification.success.caption,
                        message: 'Berhasil Proses Data.',
                        color: utils.notification.success.color,
                        icon: utils.notification.success.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'error') {
                    notifications.show({
                        id: notificationId.current.errorSubmitData,
                        title: utils.notification.warning.caption,
                        message: response.data.pesan,
                        color: utils.notification.warning.color,
                        icon: utils.notification.warning.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'jml-melebihi-data-aktual') {
                    setData(d => ({
                        ...d,
                        jmlError: 'Jumlah yang diinput melebihi jumlah yang seharusnya.'
                    }))
                }

                if (response.data.flag_status === 'prevent-zero-input') {
                    setData(d => ({
                        ...d,
                        jmlError: 'Input jumlah minimal 1'
                    }))
                }

                if (response.data.flag_status === 'no-data') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: true,
                            content: 'Data tidak ditemukan. Muat ulang halaman untuk mendapatkan data terbaru.',
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'invalid-json-token') {
                    handleSetLoginContext(null);
                }

                if (response.data.flag_status === 'user-inactive') {
                    handleSetLoginContext(null);
                }
            }).catch(error => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false }));

                notifications.show({
                    id: notificationId.current.errorSubmitData,
                    title: utils.notification.warning.caption,
                    message: error.message,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: false,
                })
            })
        }
    }

    return (
        <React.Fragment>
            <Drawer
                opened={stateShow}
                onClose={fnShow}
                withCloseButton={false}
                position="left"
                closeOnClickOutside={true}

                title={
                    <Group noWrap spacing='xs'>
                        <ActionIcon
                            size='lg'
                            variant="light"
                            radius='xl'
                            color="blue"
                            onClick={fnShow}
                        >
                            <IconArrowLeft size='1.25rem' stroke={1.5} />
                        </ActionIcon>

                        <Text>Masukkan ke Stok Rusak</Text>
                    </Group>
                }
                size='md'

                styles={() => ({
                    title: {
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                    }
                })}
            >
                <Box py='md'>
                    <Text
                        size="sm"
                        mb='2rem'
                    >
                        <StockItemData
                            stateData={stateData}
                        />
                    </Text>

                    <Box
                        my='xl'
                        p='md'

                        sx={(tema) => ({
                            backgroundColor: tema.colorScheme === 'light' ? tema.colors.gray[0] : tema.colors.dark[6],
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: tema.colorScheme === 'light' ? tema.colors.gray[1] : tema.colors.dark[5],
                            borderRadius: tema.radius.sm,
                        })}
                    >
                        <Text
                            size='sm'
                            fw='bold'
                            tt='uppercase'
                        >
                            Input jumlah yang akan dimasukkan pada item stok rusak
                        </Text>

                        <Divider my='md' />

                        <TextInput
                            mb='md'
                            variant='default'
                            placeholder="Jumlah"
                            label="Jumlah"
                            value={data.jml}
                            onChange={(val) => setData(d => ({ ...d, jml: regexNumberOnly(val.target.value) }))}
                            error={data.jmlError}
                        />

                        <Textarea
                            variant='default'
                            label='Keterangan'
                            placeholder='Keterangan'
                            withAsterisk={false}
                            minRows={3}
                            maxRows={4}

                            value={data.keterangan}
                            onChange={(e) =>
                                setData(d => ({
                                    ...d,
                                    keterangan: e.target.value
                                }))
                            }
                            error=''
                        />

                        <Button
                            fullWidth
                            mt='md'
                            variant="light"
                            color='green'
                            loading={data.submitLoading}
                            onClick={submitData}
                        >
                            Submit
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <ModalResponse
                stateShow={data.modalResponse.show}
                fnHide={() => setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        show: false
                    }
                }))}
                // stateOverlayZIndex={202}

                stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
                stateType={data.modalResponse.type}
                stateContent={data.modalResponse.content}
            />
        </React.Fragment>
    )
}

export default StockItemMoveStokRusak;